import React from 'react';

import { StorageClient } from '@atlaskit/frontend-utilities/StorageClient';
import type { UFOExperience } from '@atlassian/ufo-experimental/experience';

import { VARIANT_ORDER } from '../../common/constants';
import { NO_OP, remoteImport } from '../../common/utils';
import {
	type ComponentProps,
	type DefaultOpenProps,
	type ImportMap,
	type ManifestMapValue,
	type OnEvent,
	PluginName,
	type PluginVariant,
	type RemoteModuleConfig,
	type RemotePlugin,
} from '../../types';
import generateHash from '../generate-hash';
import { setupSystemModules } from '../remote-setup';
import { getUfoExperience } from '../use-remote-module-loader/utils';

type ModuleId = RemoteModuleConfig['moduleName'];

type Module<T = Record<string, any>> = T;

const isRemoteModuleConfig = (
	config: ManifestMapValue | undefined,
): config is RemoteModuleConfig => {
	return (config as RemoteModuleConfig)?.moduleUri !== undefined;
};

class AtlassianSystemRemoteFrontendSdk {
	// list of remote modules to be imported
	private readonly plugin?: RemotePlugin;

	// map of the above modules
	private readonly importMap: RemoteModuleConfig;

	private variant?: PluginVariant;

	// map of the remote modules that have been fetched
	private resolvedModuleMap = new Map<ModuleId, Module>();

	private readonly storageClient = new StorageClient(`remote-module-loader-bifrost-override`, {
		storageEngine: 'sessionStorage',
	});

	public readonly renderUfoExperience: UFOExperience;

	constructor(plugin: RemotePlugin) {
		this.plugin = plugin;
		this.importMap = this.findPluginManifest(plugin);

		this.setImportMaps([this.importMap]);
		this.renderUfoExperience = getUfoExperience(this.getModuleName(), 'render');
	}

	/**
	 * Using the dimensions defined in the plugin, search through the manifest and find the most applicable one
	 *
	 * @param plugin
	 * @private
	 */
	private findPluginManifest(plugin: RemotePlugin): RemoteModuleConfig | never {
		if (plugin) {
			const { manifestMap, dimensions } = plugin;

			const manifestEnv = manifestMap.get(dimensions.env);
			if (manifestEnv) {
				// This is not currently allowed in the types but is an escape hatch for not defining any variants
				if (isRemoteModuleConfig(manifestEnv)) {
					return manifestEnv;
				}

				/**
				 * If we request a `latest` dimension, but only have `stable` in our map then we use it.
				 * If we request a `stable` dimension, but only have `latest` an error will be thrown due to the variant order.
				 */
				const variantIndex = VARIANT_ORDER.indexOf(dimensions.variant);
				if (variantIndex > -1) {
					for (let i = variantIndex; i < VARIANT_ORDER.length; i++) {
						const manifestVariant = manifestEnv.get(VARIANT_ORDER[i]);
						if (isRemoteModuleConfig(manifestVariant)) {
							this.variant = VARIANT_ORDER[i]; // set the variant applied for use later

							return manifestVariant;
						}
					}

					throw new Error(
						`No valid "variant" dimensions defined in plugin for [${dimensions.variant}].`,
					);
				}

				throw new Error(
					`Invalid "variant" dimension defined in plugin for [${dimensions.variant}].`,
				);
			}

			throw new Error(`No "env" dimension defined in plugin for [${dimensions.env}].`);
		}

		throw new Error('Invalid "plugin" provided to SDK.');
	}

	public getModuleName(importMap: RemoteModuleConfig = this.importMap): string {
		if (importMap) {
			return importMap.moduleName;
		}

		return '';
	}

	private loadFromQueryAndSaveToSession(key: string): string | undefined {
		try {
			const params = new URLSearchParams(window.location.search);

			if (params.has(`${key}-clear`)) {
				this.storageClient.removeItem(key);
				return undefined;
			}

			if (params.has(key)) {
				let value = params.get(key) as string;
				this.storageClient.setItemWithExpiry(key, value, 3 * 3600 * 1000); // 3 hours in ms
				return value;
			} else {
				const itemStr = this.storageClient.getItem(key);
				if (!itemStr) {
					return undefined;
				}
				return itemStr;
			}
		} catch (error) {
			console.error(`Error loading value from query or sessionStorage: ${error}`);
			return undefined;
		}
	}

	private addBifrostOverride(url: string) {
		try {
			if (!this.plugin?.bifrostOverrideKey) {
				return url;
			}

			let bifrostOverrideValue = this.loadFromQueryAndSaveToSession(
				this.plugin?.bifrostOverrideKey,
			);

			if (!bifrostOverrideValue) {
				return url;
			}
			const urlToUpdate = new URL(url);
			if (urlToUpdate.protocol === 'https:' || urlToUpdate.protocol === 'http:') {
				let inputParams = new URLSearchParams(urlToUpdate.search);
				inputParams.set('bifrost-override', bifrostOverrideValue);
				urlToUpdate.search = inputParams.toString();
			}

			return urlToUpdate.toString();
		} catch (e) {
			console.error('Error adding bifrost override to url. Falling back to original url.');
			return url;
		}
	}

	/**
	 * If a moduleUriMapper is defined on the plugin, then we call it passing through the hash function.
	 * Otherwise return the moduleUri defined in the import map
	 *
	 * @param importMap
	 */
	public getModuleUri(importMap: RemoteModuleConfig = this.importMap): string {
		if (importMap) {
			let moduleUri = importMap.moduleUri;

			if (this.plugin?.moduleUriMapper) {
				moduleUri = this.plugin.moduleUriMapper(importMap.moduleUri, this.variant, generateHash);
			}

			if (this.plugin?.bifrostOverrideKey) {
				moduleUri = this.addBifrostOverride(moduleUri);
			}

			return moduleUri;
		}

		return '';
	}

	/**
	 * Returns the module if it has been resolved into the internal module map
	 */
	public getResolvedModule(): Record<string, any> | undefined {
		return this.resolvedModuleMap.get(this.getModuleName());
	}

	/**
	 * Returns whether the module has been registered by SystemJS AND the current SDK
	 */
	public isModuleRegisteredAndValid(): boolean {
		// a module can appear in the registry as `null` if it fails to load
		return (
			System.has(this.getModuleUri()) &&
			!!System.get(this.getModuleUri()) &&
			!!this.getResolvedModule()
		);
	}

	/**
	 * Set up our Import Maps dynamically from the provided import config.
	 * We also set the plugin.externals into the module registry so that they are shared.
	 * However, these are scoped only to our remote import.
	 *
	 * https://github.com/systemjs/systemjs/blob/main/docs/import-maps.md
	 *
	 * @param importMaps
	 */
	public setImportMaps(importMaps: RemoteModuleConfig[]): void {
		let scopedModules: Record<string, string> | null = null;

		try {
			// Set the external packages into the System registry using the locally resolved (from the Product) packages
			scopedModules = setupSystemModules(
				importMaps.flatMap((item) => item.externals),
				this.plugin?.name || PluginName.AppSwitcher,
			);
		} catch (e) {
			// TODO add logger
			console.error(e);
		}

		System.addImportMap(
			importMaps.reduce<Required<ImportMap>>(
				(acc, item) => {
					const moduleUri = this.getModuleUri(item);
					acc.imports[item.moduleName] = moduleUri;

					if (scopedModules && item.externals.length) {
						acc.scopes[moduleUri] = {};

						for (const external of item.externals) {
							const externalModuleIdentifier = 'name' in external ? external.name : external.module;

							acc.scopes[moduleUri][externalModuleIdentifier] =
								scopedModules[externalModuleIdentifier];
						}
					}

					return acc;
				},
				{ imports: {}, scopes: {} },
			),
		);
	}

	/**
	 * Fire off the request to fetch our module from the System.JS import map.
	 * It is then stored in the resolved module map for easy use later.
	 */
	public async fetchModule(): Promise<Record<string, any> | undefined> {
		if (this.importMap) {
			const { moduleName } = this.importMap;

			const module = await remoteImport<Module>(moduleName);

			this.resolvedModuleMap.set(moduleName, module);

			return module;
		}
		return undefined;
	}

	public resolveExport<T = any>(exportName?: 'default' | string): T | undefined {
		const resolvedModule = this.getResolvedModule();
		if (resolvedModule) {
			if (exportName) {
				return resolvedModule[exportName];
			}

			return resolvedModule as T;
		}
		return undefined;
	}

	/**
	 * Render our resolved module as a React component in the existing React Tree.
	 * This DOES NOT create a new root.
	 *
	 * @param exportName
	 * @param props
	 * @param fallback
	 */
	public renderComponent<P extends {} = DefaultOpenProps>(
		exportName: 'default' | string = 'default',
		props?: ComponentProps<P> | null,
		fallback: React.ReactNode | null = null,
		children: React.ReactNode | null = null,
		experience: UFOExperience | null = null,
		onEvent: OnEvent = NO_OP,
	): React.ReactNode | null {
		const component = this.resolveExport(exportName);
		if (component) {
			let componentToRender: React.ReactNode;
			if (React.isValidElement(component)) {
				componentToRender = props ? React.cloneElement(component, props, children) : component;
			} else {
				componentToRender = React.createElement<ComponentProps<P>>(component, props, children);
			}

			onEvent({ type: 'render-complete' });

			experience?.success({
				metadata: {
					moduleName: this.getModuleName(),
				},
			});

			return componentToRender;
		}
		return fallback;
	}
}

export default AtlassianSystemRemoteFrontendSdk;
